import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Bell,
  Bot,
  CircleCheck,
  CircleHelp,
  Clock,
  LayoutDashboard,
  LogOut,
  Menu,
  Tag,
  UserRound,
  UsersRound,
  X
} from 'lucide-react';
import { useLanguage } from '../assets/js/LanguageContext';
import { clearSession } from '../utils/session';
import { onUserUpdated } from '../assets/js/userEvents';
import { notificationView } from '../assets/js/notificationView';
import { notifyNotificationsRead, onNotificationsRead } from '../assets/js/notificationEvents';
import '../assets/styles/AdminHeader.css';
import '../assets/styles/AppSidebar.css';
import LogoSvg from '../assets/images/header_logo_extracted.png';
import HelpModal from './Admin-/HelpModal';
import SectionEyebrow from './common/SectionEyebrow';
import useFloatingPanel from './common/useFloatingPanel';

const LANGUAGES = [
  { value: 'es', code: 'ES', label: 'Español' },
  { value: 'en', code: 'EN', label: 'English' },
  { value: 'zh', code: '中文', label: '简体中文' }
];

const AdminHeader = () => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);
  const [userName, setUserName] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const notificationRef = useRef(null);
  const notifyTriggerRef = useRef(null);
  const notifyPanelRef = useRef(null);
  const [notifySide, setNotifySide] = useState('right');

  const navigate = useNavigate();
  const location = useLocation();
  const { t, language, setLanguage } = useLanguage();

  const getToken = () => localStorage.getItem('token');

  const fetchUserPermissions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/usuario`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userData = await response.json();
      setUserPermissions(userData.has_permissions);
      setUserName((userData.name || '').trim());
    } catch (error) {
      console.error('Error al obtener permisos del usuario:', error);
    }
  }, []);

  useEffect(() => onUserUpdated((event) => {
    const changes = event.detail || {};
    if (typeof changes.name === 'string') setUserName(changes.name.trim());
    else fetchUserPermissions();
  }), [fetchUserPermissions]);

  useEffect(() => {
    const fetchNotifications = async () => {
      const token = getToken();
      if (!token) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/notifications`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setNotifications(data);
      } catch (error) {
        console.error('Error al obtener todas las notificaciones:', error);
      }
    };

    fetchNotifications();
  }, [location.pathname]);

  useEffect(() => {
    fetchUserPermissions();

    const handleClickOutside = (event) => {
      const inside = notificationRef.current?.contains(event.target) || notifyPanelRef.current?.contains(event.target);
      if (!inside) setShowNotifications(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [fetchUserPermissions]);

  useEffect(() => {
    document.body.classList.add('tp-app-shell');
    return () => document.body.classList.remove('tp-app-shell');
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowNotifications(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('tp-sidebar-open');

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsMobileMenuOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previous;
      document.body.classList.remove('tp-sidebar-open');
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileMenuOpen]);

  // Si se leen desde la pagina de notificaciones, el numero baja aqui al momento
  useEffect(() => onNotificationsRead((event) => {
    const ids = (event.detail || {}).ids || [];
    setNotifications((prev) => prev.map((n) => (ids.includes(n.id) ? { ...n, is_read: true } : n)));
  }), []);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const markAsRead = async (notificationId) => {
    const token = getToken();
    if (!token) return;

    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
    );
    notifyNotificationsRead([notificationId]);

    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/notifications/mark-read`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notification_keys: [notificationId] })
      });
    } catch (error) {
      console.error('Error al marcar notificación como leída:', error);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: false } : n))
      );
    }
  };

  const handleHelpClick = () => {
    setIsMobileMenuOpen(false);
    setShowHelpModal(true);
  };

  const handleLogout = () => {
    setIsMobileMenuOpen(false);
    clearSession();
    window.dispatchEvent(new Event('authChange'));
    navigate('/');
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('adminheader_dashboard') },
    ...(userPermissions === 5
      ? [
          { to: '/discount-codes', icon: Tag, label: t('adminheader_discounts') },
          { to: '/ai-documents', icon: Bot, label: t('adminheader_ai_docs') }
        ]
      : []),
    ...(userPermissions === 4 || userPermissions === 5
      ? [{ to: '/admin', icon: UsersRound, label: t('adminheader_admin') }]
      : [])
  ];

  const isActive = (path) => (path === '/dashboard'
    ? location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/') || location.pathname === '/choose'
    : location.pathname === path);

  const notifyPosition = useFloatingPanel(showNotifications, notifyTriggerRef, notifyPanelRef, {
    side: notifySide,
    align: 'start',
    exactWidth: notifySide !== 'right',
    cap: 480
  });

  const toggleNotifications = () => {
    setNotifySide(window.matchMedia('(max-width: 960px)').matches ? 'vertical' : 'right');
    setShowNotifications((open) => !open);
  };

  const nameParts = userName.split(/\s+/).filter(Boolean);
  const initials = nameParts.slice(0, 2).map((part) => part[0].toUpperCase()).join('');
  const greeting = nameParts.length
    ? t('sidebar_hello').replace('{name}', nameParts[0])
    : t('sidebar_hello_anon');

  const badge = unreadCount > 0 && (
    <span className="tp-sidebar__badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
  );

  return (
    <>
      <div className="tp-appbar">
        <button
          type="button"
          className="tp-appbar__btn"
          onClick={() => setIsMobileMenuOpen(true)}
          aria-label={t('sidebar_open_menu')}
          aria-expanded={isMobileMenuOpen}
          aria-controls="tp-sidebar"
        >
          <Menu size={20} strokeWidth={2} />
        </button>
        <Link to="/" className="tp-appbar__logo-link">
          <img src={LogoSvg} alt={t('footer_logo_alt')} className="tp-appbar__logo" />
        </Link>
        <Link
          to="/notifications"
          className="tp-appbar__btn tp-appbar__bell"
          aria-label={t('adminheader_notifications')}
        >
          <Bell size={20} strokeWidth={2} />
          {badge}
        </Link>
      </div>

      <div
        className={`tp-sidebar-backdrop ${isMobileMenuOpen ? 'is-open' : ''}`.trim()}
        onClick={() => setIsMobileMenuOpen(false)}
        aria-hidden="true"
      />

      <aside
        id="tp-sidebar"
        className={`tp-sidebar ${isMobileMenuOpen ? 'is-open' : ''}`.trim()}
        aria-label={t('sidebar_nav_label')}
      >
        <div className="tp-sidebar__top">
          <div className="tp-sidebar__brand">
            <Link to="/" className="tp-sidebar__logo-link">
              <img src={LogoSvg} alt={t('footer_logo_alt')} className="tp-sidebar__logo" />
            </Link>
            <SectionEyebrow className="tp-sidebar__area">{t('dash_title')}</SectionEyebrow>
          </div>
          <button
            type="button"
            className="tp-sidebar__close"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label={t('sidebar_close_menu')}
          >
            <X size={20} strokeWidth={2} />
          </button>
        </div>

        <nav className="tp-sidebar__nav">
          <ul className="tp-sidebar__list">
            {navItems.map(({ to, icon: Icon, label }) => (
              <li key={to}>
                <Link
                  to={to}
                  className={`tp-sidebar__link ${isActive(to) ? 'is-active' : ''}`.trim()}
                  aria-current={isActive(to) ? 'page' : undefined}
                >
                  <Icon size={18} strokeWidth={1.9} aria-hidden="true" />
                  <span>{label}</span>
                </Link>
              </li>
            ))}

            <li className="tp-sidebar__notifications" ref={notificationRef}>
              <button
                ref={notifyTriggerRef}
                type="button"
                className={`tp-sidebar__link ${showNotifications || isActive('/notifications') ? 'is-active' : ''}`.trim()}
                onClick={toggleNotifications}
                aria-expanded={showNotifications}
                aria-haspopup="true"
              >
                <Bell size={18} strokeWidth={1.9} aria-hidden="true" />
                <span>{t('adminheader_notifications')}</span>
                {badge}
              </button>

              {showNotifications && createPortal(
                <div className="tp-notify" ref={notifyPanelRef} style={notifyPosition.style}>
                  <div className="tp-notify__head">
                    <p className="tp-notify__title">{t('adminheader_notifications')}</p>
                    <Link to="/notifications" className="tp-notify__all">{t('adminheader_view_all')}</Link>
                  </div>
                  {notifications.length > 0 ? (
                    <ul className="tp-notify__list">
                      {notifications.map((notification) => {
                        const view = notificationView(notification, t, language);
                        return (
                        <li
                          key={notification.id}
                          className={`tp-notify__item ${notification.is_read ? '' : 'is-unread'}`.trim()}
                        >
                          <Link
                            to={view.href || '/notifications'}
                            className="tp-notify__link"
                            onClick={() => {
                              setShowNotifications(false);
                              if (!notification.is_read) markAsRead(notification.id);
                            }}
                          >
                            <span className={`tp-notify__icon is-${view.kind}`} aria-hidden="true">
                              {view.kind === 'plan' && <Clock size={16} strokeWidth={2} />}
                              {view.kind === 'survey' && <BarChart3 size={16} strokeWidth={2} />}
                              {view.kind === 'done' && <CircleCheck size={16} strokeWidth={2} />}
                            </span>
                            <span className="tp-notify__text">
                              <span className="tp-notify__name">{view.title}</span>
                              <span className="tp-notify__date">
                                {new Date(notification.date_event).toLocaleString()}
                              </span>
                            </span>
                          </Link>
                        </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="tp-notify__empty">{t('adminheader_no_notifications')}</p>
                  )}
                </div>,
                document.body
              )}
            </li>
          </ul>
        </nav>

        <div className="tp-sidebar__bottom">
          <Link
            to="/profile"
            className={`tp-sidebar__user ${isActive('/profile') ? 'is-active' : ''}`.trim()}
          >
            <span className="tp-sidebar__avatar" aria-hidden="true">
              {initials || <UserRound size={16} strokeWidth={2} />}
            </span>
            <span className="tp-sidebar__user-text">
              <span className="tp-sidebar__user-name">{greeting}</span>
              <span className="tp-sidebar__user-sub">{t('sidebar_view_profile')}</span>
            </span>
          </Link>

          <button type="button" className="tp-sidebar__link" onClick={handleHelpClick}>
            <CircleHelp size={18} strokeWidth={1.9} aria-hidden="true" />
            <span>{t('adminheader_help')}</span>
          </button>

          <div className="tp-sidebar__lang" role="group" aria-label={t('sidebar_language')}>
            {LANGUAGES.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`tp-sidebar__lang-btn ${option.value === language ? 'is-active' : ''}`.trim()}
                aria-pressed={option.value === language}
                aria-label={option.label}
                onClick={() => setLanguage(option.value)}
              >
                {option.code}
              </button>
            ))}
          </div>

          <button type="button" className="tp-sidebar__link tp-sidebar__logout" onClick={handleLogout}>
            <LogOut size={18} strokeWidth={1.9} aria-hidden="true" />
            <span>{t('adminheader_logout')}</span>
          </button>
        </div>
      </aside>

      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        showOnFirstVisit={false}
      />
    </>
  );
};

export default AdminHeader;
